import React from "react";
import { IconButton } from "@mui/material";

type TabLabels = Record<string, string>;

type ExtraColumnLabels = Record<string, string>;
const NAME_LABEL_BY_TAB: Record<string, string> = {
    rooms: "Room Name",
    zones: "Zone Name",
    groups: "Group Name",
    hubs: "Hub Name",
    sensors: "Sensor Name",
    controllers: "Name",
    auxiliaryHubs: "Name",
};

interface GetColumnLabelParams {
    col: string;
    tabKey: string;
    tabLabels: TabLabels;
    isRefreshing: boolean;
    refreshIcon: string;
    onRefreshSensors: () => void;
    onRefreshControllers: () => void;
    extraColumnLabels?: ExtraColumnLabels;
}

const formatColumnLabel = (col: string): string =>
    col
        .replaceAll(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase());

export const getNetworkColumnLabel = ({
    col,
    tabKey,
    tabLabels,
    isRefreshing,
    refreshIcon,
    onRefreshSensors,
    onRefreshControllers,
    extraColumnLabels,
}: GetColumnLabelParams): React.ReactNode => {
    const colLower = col.toLowerCase();
    const overrideKey = `${tabKey}:${colLower}`;
    const overrideLabel = extraColumnLabels?.[overrideKey];
    if (overrideLabel) {
        return overrideLabel;
    }

    if (col === "name" || col === "sensor_name") {
        return NAME_LABEL_BY_TAB[tabKey] ?? "Name";
    }

    if (colLower === "minmax" || colLower.includes("minmax")) {
        return "Agg Min / Max (Last 24 hrs)";
    }

    if (colLower === "lastactive" || colLower.includes("lastactive")) {
        return (
            <span style={{ alignItems: "center", paddingLeft: "10px" }}>
                <span>Last Active</span>
                {tabKey === "sensors" && (
                    <IconButton
                        disableRipple
                        className={
                            isRefreshing
                                ? "mr-2 bigRefresh refresh-animation"
                                : "mr-2 bigRefresh"
                        }
                        style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            marginLeft: 8,
                        }}
                        aria-label="Refresh all sensor readings"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRefreshSensors();
                        }}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            onRefreshSensors();
                        }}
                    >
                        <img
                            src={refreshIcon}
                            alt="refresh"
                        />
                    </IconButton>
                )}
            </span>
        );
    }

    if (colLower === "status") {
        return (
            <span>
                <span>{formatColumnLabel(col)}</span>
                {tabKey === "controllers" && (
                    <IconButton
                        disableRipple
                        className={
                            isRefreshing
                                ? "mr-2 bigRefresh refresh-animation"
                                : "mr-2 bigRefresh"
                        }
                        style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            marginLeft: 8,
                        }}
                        aria-label="Refresh all controller statuses"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRefreshControllers();
                        }}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            onRefreshControllers();
                        }}
                    >
                        <img
                            src={refreshIcon}
                            alt="refresh"
                        />
                    </IconButton>
                )}
            </span>
        );
    }

    return formatColumnLabel(col);
};
